"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { locales, localeLabel, HINDI_SLUGS } from "@/i18n/locales";
import { detectLocale, getLocalePath } from "@/i18n";

export default function LanguageToggle() {
  const pathname = usePathname();
  const activeLocale = detectLocale(pathname);
  // Strip the locale prefix → [slug, ...subPath]. The slug (first segment) may
  // carry a per-locale alias; deeper segments (e.g. the planet on
  // /lal-kitab-remedies/<planet>/) are locale-invariant and MUST be preserved so
  // the toggle targets the page's actual counterpart, not the parent hub — which
  // can 301 (e.g. /lal-kitab-remedies/ → /lal-kitab/) and trip the live-4xx audit.
  const segments = pathname.split("/").filter(Boolean);
  const pathSegments = segments[0] === "hi" ? segments.slice(1) : segments;
  // For non-Hindi pages, default to rashifal as the landing slug
  const slug = pathSegments[0] || "rashifal";
  const subPath = pathSegments.slice(1).join("/");

  return (
    <div className="flex gap-1.5">
      {locales.map((locale) => {
        if (locale === activeLocale) return null;
        const hasHindi = HINDI_SLUGS.has(slug);
        const base =
          locale === "hi"
            ? hasHindi
              ? getLocalePath(slug, "hi")
              : "/hi/aaj-ka-rashifal/"
            : getLocalePath(slug, "en");
        // Preserve the nested sub-path ONLY when switching to English: every hi
        // nested page is a localization of a real en page, so /hi/X/Y/ → /X/Y/
        // resolves (this kills the lal-kitab hub 301). The reverse is NOT true —
        // most en nested pages (baby-names/<nak>, choghadiya/<city>, …) have no hi
        // counterpart, so for →hi we drop to the localized hub (a guaranteed 200)
        // rather than invent a /hi/X/Y/ that 404s (the 80-link audit regression).
        const keepSubPath = Boolean(subPath) && locale === "en";
        const href = keepSubPath ? `${base.replace(/\/$/, "")}/${subPath}/` : base;
        const titleAttr = locale === "hi" ? "हिंदी में पढ़ें" : "Switch to English";
        return (
          <Link
            key={locale}
            href={href}
            className="text-[12px] font-semibold px-2.5 py-1.5 rounded-md transition"
            style={{
              color: "var(--ish-text-muted)",
              border: "1px solid var(--ish-border)",
            }}
            title={titleAttr}
          >
            {localeLabel[locale]}
          </Link>
        );
      })}
    </div>
  );
}
